import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SaleType, SALE_TYPE_LABELS, SOURCE_WEBSITE_LABELS, SOURCE_WEBSITE_COLORS, SOURCE_WEBSITES, SourceWebsite } from '../types';

export interface ActiveFilters {
  saleType: SaleType | 'all';
  dateRange: 'all' | 'this_weekend' | 'this_week' | 'upcoming';
  sourceWebsite: SourceWebsite | 'all';
}

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  activeFilters: ActiveFilters;
  onApply: (filters: ActiveFilters) => void;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  activeFilters,
  onApply,
}) => {
  const [localFilters, setLocalFilters] = useState(activeFilters);

  React.useEffect(() => {
    setLocalFilters(activeFilters);
  }, [activeFilters]);

  const handleApply = () => {
    onApply(localFilters);
  };

  const handleReset = () => {
    const resetFilters: ActiveFilters = {
      saleType: 'all',
      dateRange: 'all',
      sourceWebsite: 'all',
    };
    setLocalFilters(resetFilters);
  };

  const saleTypeOptions: Array<{ value: SaleType | 'all'; label: string }> = [
    { value: 'all', label: 'All Sales' },
    { value: 'estate_sale', label: 'Estate Sales' },
    { value: 'moving_sale', label: 'Moving Sales' },
    { value: 'multi_family', label: 'Multi-Family' },
    { value: 'fundraiser', label: 'Fundraisers' },
    { value: 'yard_sale', label: 'Yard Sales' },
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'Any Time' },
    { value: 'this_weekend', label: 'This Weekend' },
    { value: 'this_week', label: 'This Week' },
    { value: 'upcoming', label: 'Upcoming' },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 360 },
          p: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Filters
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Sale Type
      </Typography>
      <FormGroup sx={{ mb: 3 }}>
        {saleTypeOptions.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                checked={localFilters.saleType === option.value}
                onChange={() => setLocalFilters({ ...localFilters, saleType: option.value })}
              />
            }
            label={option.label}
          />
        ))}
      </FormGroup>
      
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        When
      </Typography>
      <FormControl fullWidth sx={{ mb: 3 }}>
        <Select
          value={localFilters.dateRange}
          onChange={(e) => setLocalFilters({ 
            ...localFilters, 
            dateRange: e.target.value as 'all' | 'this_weekend' | 'this_week' | 'upcoming' 
          })}
          sx={{ borderRadius: 2 }}
        >
          {dateRangeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {/* Source Website — dev/localhost only */}
      {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
        <>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Source Website
      </Typography>
      <FormGroup sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={localFilters.sourceWebsite === 'all'}
              onChange={() => setLocalFilters({ ...localFilters, sourceWebsite: 'all' })}
            />
          }
          label="All Sources"
        />
        {SOURCE_WEBSITES.map((website) => {
          const color = SOURCE_WEBSITE_COLORS[website];
          return (
            <FormControlLabel
              key={website}
              control={
                <Checkbox
                  checked={localFilters.sourceWebsite === website}
                  onChange={() => setLocalFilters({ ...localFilters, sourceWebsite: website })}
                  sx={{
                    color: color,
                    '&.Mui-checked': { color: color },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                  {SOURCE_WEBSITE_LABELS[website]}
                </Box>
              }
            />
          );
        })}
      </FormGroup>
        </>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 'auto', pt: 2 }}>
        <Button 
          variant="outlined" 
          onClick={handleReset}
          sx={{ flex: 1 }}
        >
          Reset
        </Button>
        <Button 
          variant="contained" 
          onClick={handleApply}
          sx={{ flex: 1 }}
        >
          Apply
        </Button>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;
